import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { ApiError } from "../api";

/** Platform admin login — for the single operator who runs the cloud VPS.
 *  Tenant owners/managers/viewers belong on /login; landing here with a
 *  tenant-only account logs them back out and shows a prompt to the correct
 *  page. Kept visually distinct (darker card, shield icon) so the operator
 *  immediately knows they're on the right page. */
export function AdminLogin() {
  const { user, loading, adminLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(
    params.get("redirected") === "admin"
      ? "Tài khoản này là admin — đăng nhập ở đây thay vì trang tenant."
      : null,
  );
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="login-shell">Đang tải…</div>;
  if (user) {
    return <Navigate to={user.is_platform_admin ? "/admin" : "/"} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // adminLogin endpoint already enforces is_platform_admin server-side,
      // so if the call succeeds we know this user is an admin — no need to
      // re-check client-side.
      await adminLogin(username, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // 401 from /admin-login means "not admin OR wrong creds" — same
        // generic message either way (endpoint doesn't leak which one).
        setError(err.message || "Đăng nhập thất bại");
      } else {
        setError("Không kết nối được tới cloud portal");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell" style={{ background: "#0f172a" }}>
      <div
        className="card login-card"
        style={{
          background: "#1e293b",
          color: "#e2e8f0",
          borderColor: "#334155",
        }}
      >
        <h2 style={{ margin: "0 0 4px 0", color: "#e2e8f0" }}>
          🛡️ Platform Admin
        </h2>
        <p style={{ marginTop: 0, color: "#94a3b8", fontSize: "0.875rem" }}>
          Chỉ dành cho người vận hành cloud VPS. Owner/manager/viewer vui lòng
          dùng <Link to="/login" style={{ color: "#60a5fa" }}>trang tenant</Link>.
        </p>

        <form onSubmit={onSubmit}>
          {error && <div className="alert error" style={{ marginTop: 12 }}>{error}</div>}

          <div className="field">
            <label style={{ color: "#94a3b8" }}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              placeholder="admin"
              style={{ background: "#0f172a", color: "#e2e8f0", borderColor: "#334155" }}
            />
          </div>

          <div className="field">
            <label style={{ color: "#94a3b8" }}>Mật khẩu</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={{ background: "#0f172a", color: "#e2e8f0", borderColor: "#334155" }}
            />
          </div>

          <button
            type="submit"
            className="primary"
            disabled={submitting}
            style={{ width: "100%", background: "#3b82f6", borderColor: "#3b82f6" }}
          >
            {submitting ? "Đang đăng nhập…" : "Đăng nhập admin"}
          </button>
        </form>

        <p style={{ marginTop: 16, marginBottom: 0, color: "#64748b", fontSize: "0.75rem" }}>
          Mặc định: <code style={{ background: "#0f172a", padding: "1px 4px", borderRadius: 3 }}>admin / admin</code> — đổi password ngay sau khi login.
        </p>
      </div>
    </div>
  );
}
